"use client";

import React, { useState, useEffect, useMemo, useRef } from "react";
import DropdownPrice from "@/components/DropdownPrice";
import SizeDropdown from "@/components/SizeDropdown";
import { useMediaQuery } from "react-responsive";
import useModalFocusAndScrollLock from "@/hooks/useModalFocusAndScrollLock";
import { useMobileHistoryState } from "@/hooks/useMobileHistoryState";
import Modal from "@/components/Modal";
import AuthSignInButton from "@/components/AuthSignInButton";
import { trackEvent } from "@/lib/client/analytics";

import {
  typeSelection,
  countrySelection,
  currencySelection,
  sortSelecton,
  relevanceSortOption,
  imageSearchSortSelection,
  getVendorType,
} from "@/lib/shared/constants";
import { useGlobalData } from "@/context/globalContext";
import { useAuthContext } from "@/context/authContext";
import { useFiltersStore } from "@/stores/filtersStore";
import { useImageSearchStore } from "@/stores/imageSearchStore";
import { useResumeSearch } from "@/hooks/useResumeSearch";
import { useItemsStore } from "@/stores/itemsStore";
import { useCollectionsStore } from "@/stores/collectionsStore";
import { useUiStore } from "@/stores/uiStore";
import { IconCheck, IconHeart, IconHelpCircle, IconRefresh, IconX } from "@tabler/icons-react";
import useSavedFilters from "@/hooks/useSavedFilters";
import useResetSearch from "@/hooks/useResetSearch";
import FilterAutocomplete from "@/components/FilterAutocomplete";
import SavedInput from "@/components/SaveInput";
import GroupedDropdown from "@/components/GroupedDropdown";
import NewDropdown from "@/components/NewDropdown";
import KurboButton from "@/components/KurboButton";
import {
  ActionIcon,
  Tooltip,
  Select,
  Text,
  type ComboboxItem,
  type ComboboxLikeRenderOptionInput,
  Box as MantineBox,
  Text as MantineText,
  Title as MantineTitle,
  UnstyledButton as MantineUnstyledButton,
} from "@mantine/core";
import { TOKEN_UI_ENABLED } from "@/lib/shared/featureFlags";
import { formatLastViewed } from "@/lib/shared/date";
import Tag from "./tag";
import InfoIcon from "@/icons/info";
import ItemCount from "@/components/ItemCount";
import ImageSearchCount from "@/components/ImageSearchCount";
import PriceIcon from "@/icons/Price";
import TapeIcon from "@/icons/Tape";
import { SlideIn } from "@/components/SlideIn";
import { useSearchTips } from "@/hooks/useSearchTips";
import SearchTipsModal from "@/components/SearchTipsModal";

import styles from "./styles.module.scss";

const baseQuickActionTooltipProps = {
  offset: 8,
  openDelay: 350,
  position: "bottom" as const,
  withArrow: true,
  zIndex: 10000,
};

const hiddenDefaultFilters = [
  { included: true, label: "USD", value: "USD" },
  { included: true, label: "$0 to 3,000+", value: "0,Infinity" },
  { included: true, label: "Newest", value: "newest" },
];

const isInDefaultFilters = (
  filter: Filter | FilterOption,
  defaultFilters: typeof hiddenDefaultFilters
) => {
  return !!defaultFilters?.some(
    (defaultFilter) =>
      filter?.included === defaultFilter.included &&
      filter?.label === defaultFilter.label &&
      filter?.value === defaultFilter.value
  );
};

const renderCheckedSelectOption = ({
  option,
  checked,
}: ComboboxLikeRenderOptionInput<ComboboxItem>) => (
  <MantineBox className={styles.selectOptionContent}>
    {checked && <IconCheck width="16px" height="16px" className={styles.selectCheckIcon} />}
    <Text size="xs" ml="24px">
      {option.label}
    </Text>
  </MantineBox>
);

type FiltersProps = {
  imageSearchMode?: boolean;
  onReset?: () => void;
};

const Filters = ({ imageSearchMode = false, onReset }: FiltersProps) => {
  const { isSignedIn, user, isProUser } = useAuthContext();
  const { isOpen: tipsOpen, open: openTips, close: closeTips } = useSearchTips();
  const [hasMounted, setHasMounted] = useState(false);
  const isMobile = useMediaQuery({ query: "(max-width: 1250px)" });
  const quickActionTooltipProps = useMemo(
    () => ({
      ...baseQuickActionTooltipProps,
      disabled: isMobile,
      events: {
        hover: !isMobile,
        focus: !isMobile,
        touch: false,
      },
    }),
    [isMobile]
  );
  const [openModal, setOpenModal] = useState(false);
  const excludeRelists = useFiltersStore((state) => state.excludeRelists);
  const excludeAuctions = useFiltersStore((state) => state.excludeAuctions);
  const { setExcludeRelists, setExcludeAuctions } = useFiltersStore((state) => state.actions);
  const { resumeFrom, lastViewedAt, showNew, showResume } = useResumeSearch();

  const { updateSavedFilter } = useSavedFilters();

  const activeFilters = useFiltersStore((state) => state.activeFilters);
  const searchText = useFiltersStore((state) => state.searchText);
  const savedFilters = useFiltersStore((state) => state.savedFilters);
  const savedFilterId = useFiltersStore((state) => state.savedFilterId);
  const {
    updateActiveFilter,
    removeFilter,
    updateKeywordSearch: updateKeywordSearchStore,
  } = useFiltersStore((state) => state.actions);

  const imageSearchSort = useImageSearchStore((state) => state.sort);
  const { setSort: setImageSearchSort } = useImageSearchStore((state) => state.actions);
  const resetSearch = useResetSearch();

  const filterPaneOpen = useUiStore((state) => state.filterPaneOpen);
  const { toggleFavoritesModal, setFilterPaneOpen } = useUiStore((state) => state.actions);

  const itemMinPrice = useItemsStore((state) => state.itemMinPrice);
  const itemMaxPrice = useItemsStore((state) => state.itemMaxPrice);
  const { loadInitialResults: loadInitialResultsStore } = useItemsStore((state) => state.actions);
  const favoriteCount = useCollectionsStore((state) => state.activeCollectionItems?.length ?? 0);

  const { brands, vendors, exchangeRates } = useGlobalData();

  // Track initial filter state when pane opens to detect changes
  const initialFiltersRef = useRef<string>("");

  useEffect(() => {
    setHasMounted(true);
  }, []);

  // Capture filter state when pane opens
  // Note: We intentionally exclude activeFilters and searchText from deps
  // We only want to capture the initial state when the pane opens, not update continuously
  useEffect(() => {
    if (filterPaneOpen) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { currency, ...filtersWithoutCurrency } = activeFilters;
      initialFiltersRef.current = JSON.stringify(filtersWithoutCurrency) + searchText;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterPaneOpen]);

  const handleCloseFilterPane = () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { currency, ...filtersWithoutCurrency } = activeFilters;
    const currentFiltersKey = JSON.stringify(filtersWithoutCurrency) + searchText;
    const filtersChanged = currentFiltersKey !== initialFiltersRef.current;

    setFilterPaneOpen(false);

    if (filtersChanged) {
      setTimeout(() => {
        window?.scrollTo(0, 0);
        document.querySelector("html")?.scrollTo(0, 0);
      }, 300);
    }
  };

  const toggleModal = () => {
    setOpenModal((prev) => !prev);
  };

  const focusTrapRef = useModalFocusAndScrollLock({
    isOpen: isMobile && filterPaneOpen,
    shouldTrapFocus: isMobile && filterPaneOpen,
    shouldLockScroll: isMobile && filterPaneOpen,
  });

  // Handle browser/native back navigation for the mobile and tablet filter sheet.
  useMobileHistoryState({
    isOpen: filterPaneOpen,
    onClose: handleCloseFilterPane,
    modalId: "filter-menu",
  });

  const filteredActiveFilters = useMemo(() => {
    return Object?.entries(activeFilters)
      .filter(([key]) => key !== "id" && key !== "available" && key !== "sort")
      .flatMap(([key, value]) =>
        (value as FilterOption[])?.map?.((option: FilterOption) => ({
          dropdown: key,
          option: option,
        }))
      )
      .filter((item) => !isInDefaultFilters(item?.option, hiddenDefaultFilters));
  }, [activeFilters]);

  const canResumeSearch = isSignedIn && !!savedFilterId && showResume;

  const vendorSelection = useMemo(
    () =>
      vendors?.map((vendor) => ({
        value: vendor.id,
        label: vendor.label,
        state: "include",
        id: vendor.id,
        group: getVendorType(vendor.type) || undefined,
      })) || [],
    [vendors]
  ) as unknown as Filter[];

  const textSearchSortSelection = useMemo(
    () => (searchText ? [relevanceSortOption, ...sortSelecton] : sortSelecton),
    [searchText]
  );

  const selectedTextSearchSort = (activeFilters["sort"]?.[0]?.value as string) || "newest";
  const displayedTextSearchSort =
    !searchText && selectedTextSearchSort === relevanceSortOption.value
      ? "newest"
      : selectedTextSearchSort;

  useEffect(() => {
    if (!hasMounted) return;

    if (isMobile && filterPaneOpen) {
      setFilterPaneOpen(false);
    }
    if (!isMobile && !filterPaneOpen) {
      setFilterPaneOpen(true);
    }

    // only close on isMobile change, not when filterPaneOpen changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, hasMounted]);

  const handleFavoritesClick = () => {
    toggleFavoritesModal();
  };

  const handleFavoriteSignInClick = () => {
    try {
      trackEvent("sign_in_prompt_shown", "session", {
        trigger: "save_favorites_button",
        page: "filter_menu",
      });
    } catch {
      // Analytics must never affect sign-in
    }
  };

  const handleResetClick = () => {
    resetSearch();
    onReset?.();
  };

  if (!hasMounted) {
    return (
      <>
        <MantineBox className={styles.placeholder} />
      </>
    );
  }

  return (
    <>
      <Modal isOpen={openModal} closeModal={toggleModal}>
        <MantineBox>
          <MantineTitle order={3}>Can't find the brand you want?</MantineTitle>
          <br />
          Our selection of brands is curated by the KURB team in response to the community needs. If
          there is a brand you would like added, please feel free to reach out to us.
        </MantineBox>
      </Modal>
      <SlideIn
        active={filterPaneOpen}
        onClose={handleCloseFilterPane}
        side="left"
        width="full"
        offsetTop={false}
        overflow="hidden"
        padding="none"
        animate={false}
        hideDefaultClose={true}
        modal={isMobile}
        modalLabel="Search filters"
        backdropLabel="Close search filters"
        className={styles.filters}
        id="filter-menu"
        dataAttrs={{
          "data-hasfilters": !!(
            filteredActiveFilters?.length ||
            searchText ||
            excludeRelists ||
            excludeAuctions
          ),
        }}
        ref={focusTrapRef}
      >
        <MantineBox className={`${styles.closeContainer} ${styles.onlyMobile}`}>
          <MantineText>FILTERS</MantineText>
          <ActionIcon
            onClick={handleCloseFilterPane}
            variant="subtle"
            radius="sm"
            className={styles.closeIcon}
            data-autofocus={false}
            size={44}
            aria-label="Close filters"
          >
            <IconX size={18} style={{ color: "black" }} />
          </ActionIcon>
        </MantineBox>
        <SearchTipsModal opened={tipsOpen} onClose={closeTips} isProUser={isProUser} />
        <MantineBox className={styles.stickySection}>
          <MantineBox className={styles.itemCount}>
            {imageSearchMode ? <ImageSearchCount /> : <ItemCount />}
          </MantineBox>
          <MantineBox
            className={styles.filterIconToolbar}
            role="toolbar"
            aria-label="Filter quick actions"
          >
            <MantineBox className={styles.utilityIconGroup}>
              <Tooltip label="Open search tips" {...quickActionTooltipProps}>
                <ActionIcon
                  variant="transparent"
                  radius="sm"
                  className={styles.filterIconButton}
                  onClick={openTips}
                  aria-label="Open search tips"
                >
                  <MantineText span className={styles.helpIconWrap} aria-hidden="true">
                    <IconHelpCircle className={styles.helpIcon} size={20} stroke={1.8} />
                  </MantineText>
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Reset filters" {...quickActionTooltipProps}>
                <ActionIcon
                  variant="transparent"
                  radius="sm"
                  className={styles.filterIconButton}
                  onClick={handleResetClick}
                  aria-label="Reset filters"
                >
                  <IconRefresh className={styles.refreshIcon} size={20} stroke={1.8} />
                </ActionIcon>
              </Tooltip>
              <Tooltip
                label={
                  isSignedIn
                    ? favoriteCount > 0
                      ? `View favorites (${favoriteCount})`
                      : "View favorites"
                    : "Sign in to save favorites"
                }
                {...quickActionTooltipProps}
              >
                <MantineText span className={styles.filterIconButtonWrap}>
                  {isSignedIn ? (
                    <>
                      <ActionIcon
                        variant="transparent"
                        radius="sm"
                        className={styles.filterIconButton}
                        onClick={handleFavoritesClick}
                        aria-label={
                          favoriteCount > 0 ? `View favorites (${favoriteCount})` : "View favorites"
                        }
                      >
                        <IconHeart className={styles.favoriteIcon} size={20} stroke={1.8} />
                      </ActionIcon>
                      {favoriteCount > 0 && (
                        <MantineText span className={styles.iconBadge}>
                          {favoriteCount}
                        </MantineText>
                      )}
                    </>
                  ) : (
                    <AuthSignInButton>
                      {/* eslint-disable-next-line no-restricted-syntax -- Clerk must clone a native element. */}
                      <button
                        type="button"
                        className={styles.filterIconButton}
                        onClick={handleFavoriteSignInClick}
                        aria-label="Sign in to save favorites"
                      >
                        <IconHeart className={styles.favoriteIcon} size={20} stroke={1.8} />
                      </button>
                    </AuthSignInButton>
                  )}
                </MantineText>
              </Tooltip>
            </MantineBox>
            {TOKEN_UI_ENABLED && (
              <Tooltip
                label="KURBO is KURB's real-time search layer. It surfaces listings added in the last hour that match your filters, before the crowd finds them."
                multiline
                maw={280}
                {...quickActionTooltipProps}
              >
                <MantineBox className={styles.kurboToolbarButton}>
                  <KurboButton variant="toolbar" />
                </MantineBox>
              </Tooltip>
            )}
          </MantineBox>
          {!imageSearchMode && <SavedInput maxSaved={isSignedIn ? 20 : 10} />}
          {!imageSearchMode && canResumeSearch && (
            <MantineBox className={styles.resumeRow}>
              <Tooltip
                label="Continue where you left off, shows items you haven't scrolled past yet"
                openDelay={400}
                withArrow
                multiline
                maw={200}
                zIndex={10000}
              >
                <MantineUnstyledButton
                  className={styles.resumeTextButton}
                  onClick={() => {
                    loadInitialResultsStore({
                      vendors: vendors as VendorFilter[],
                      brands: brands as BrandFilter[],
                      exchangeRates: exchangeRates!,
                      activeFilters,
                      searchText,
                      savedFilterId: savedFilterId ?? undefined,
                      resumeFrom: resumeFrom ?? undefined,
                      skipTotalOnly: true,
                    });
                    if (isMobile) handleCloseFilterPane();
                  }}
                >
                  Continue from {formatLastViewed(lastViewedAt)}{" "}
                  <MantineText span aria-hidden="true">
                    →
                  </MantineText>
                </MantineUnstyledButton>
              </Tooltip>
              {/* NEW RESULTS logic preserved — hidden per product decision */}
              {false && showNew && (
                <MantineUnstyledButton
                  className={styles.resumeButton}
                  onClick={() =>
                    loadInitialResultsStore({
                      vendors: vendors as VendorFilter[],
                      brands: brands as BrandFilter[],
                      exchangeRates: exchangeRates!,
                      activeFilters,
                      searchText,
                      savedFilterId: savedFilterId ?? undefined,
                      newFrom: resumeFrom ?? undefined,
                      skipTotalOnly: true,
                    })
                  }
                >
                  NEW RESULTS
                </MantineUnstyledButton>
              )}
            </MantineBox>
          )}
        </MantineBox>
        <MantineBox className={styles.scrollableContent}>
          <MantineBox className={styles.dropdownContainer}>
            <FilterAutocomplete />
            {imageSearchMode ? (
              <Select
                label="SORT BY"
                value={imageSearchSort || "best_match"}
                onChange={(val) => setImageSearchSort((val as string) || "best_match")}
                data={imageSearchSortSelection.map((o) => ({
                  label: o.label as string,
                  value: o.value as string,
                }))}
                size="sm"
                chevronColor="black"
                allowDeselect={false}
                maxDropdownHeight={250}
                renderOption={renderCheckedSelectOption}
                withCheckIcon={false}
                comboboxProps={{ position: "bottom", middlewares: { flip: true, shift: false } }}
              />
            ) : (
              <Select
                label="SORT BY"
                value={displayedTextSearchSort}
                onChange={(val) => {
                  const opt = textSearchSortSelection.find((o) => o.value === val);
                  if (opt) updateActiveFilter("sort", [{ ...opt, state: "include" }]);
                }}
                data={textSearchSortSelection.map((o) => ({
                  label: o.label as string,
                  value: o.value as string,
                }))}
                size="sm"
                chevronColor="black"
                allowDeselect={false}
                maxDropdownHeight={250}
                renderOption={renderCheckedSelectOption}
                withCheckIcon={false}
                comboboxProps={{ position: "bottom", middlewares: { flip: true, shift: false } }}
              />
            )}
            <GroupedDropdown
              label="BRAND"
              placeholder="Raf Simons, Dior..."
              options={brands}
              filterType="brands"
              onSelect={updateActiveFilter}
              activeFilters={activeFilters["brands"]}
              icon={!imageSearchMode ? <InfoIcon /> : undefined}
              onInfoClick={
                !imageSearchMode
                  ? () => {
                      toggleModal();
                    }
                  : undefined
              }
            />
            <SizeDropdown
              icon={<TapeIcon color="#000" size={30} />}
              activeFilters={activeFilters["size"]}
              selectedTypes={activeFilters["type"]}
              onSelect={updateActiveFilter}
            />
            <GroupedDropdown
              label="CATEGORY"
              placeholder="Top, Bottoms..."
              options={typeSelection}
              filterType="type"
              onSelect={updateActiveFilter}
              activeFilters={activeFilters["type"]}
            />
            <GroupedDropdown
              label="VENDOR"
              placeholder="Depop, Mercari..."
              options={vendorSelection}
              filterType="vendors"
              onSelect={updateActiveFilter}
              activeFilters={activeFilters["vendors"]}
              groupSortOrder={["Marketplace", "Chains", "Independent Seller", "Auction House"]}
            />
            <GroupedDropdown
              label="COUNTRY"
              placeholder="South Korea, Italy..."
              options={countrySelection}
              filterType="regions"
              onSelect={updateActiveFilter}
              activeFilters={activeFilters["regions"]}
              groupSortOrder={[
                "North America",
                "Europe",
                "Oceania",
                "Asia",
                "South America",
                "Africa",
              ]}
            />
            <Select
              label="AVAILABILITY"
              value={
                activeFilters["available"]?.[0]?.value === 0
                  ? "0"
                  : activeFilters["available"]?.[0]?.value === 1
                    ? "1"
                    : "all"
              }
              onChange={(val) => {
                if (!val || val === "all") {
                  updateActiveFilter("available", []);
                } else {
                  updateActiveFilter("available", [
                    {
                      id: Number(val),
                      value: Number(val),
                      label: val === "1" ? "Available" : "Unavailable",
                      state: "include" as const,
                    },
                  ]);
                }
              }}
              data={[
                { label: "Available", value: "1" },
                { label: "Unavailable", value: "0" },
                { label: "All", value: "all" },
              ]}
              size="sm"
              chevronColor="black"
              allowDeselect={false}
              maxDropdownHeight={250}
              renderOption={renderCheckedSelectOption}
              withCheckIcon={false}
              comboboxProps={{ position: "bottom", middlewares: { flip: true, shift: false } }}
            />
            <NewDropdown
              label="CURRENCY"
              options={currencySelection}
              filterType="currency"
              onSelect={updateActiveFilter}
              activeFilters={activeFilters["currency"]}
              allowExclude={false}
            />
            <MantineBox className={styles.filterToggleGroup}>
              <MantineBox className={styles.filterToggle}>
                <MantineUnstyledButton
                  type="button"
                  role="switch"
                  aria-checked={excludeRelists}
                  className={styles.filterToggleButton}
                  onClick={() => setExcludeRelists(!excludeRelists)}
                >
                  <MantineText
                    span
                    className={`${styles.toggleTrack}${excludeRelists ? ` ${styles.toggleTrackActive}` : ""}`}
                  >
                    <MantineText span className={styles.toggleKnob} />
                  </MantineText>
                  Exclude Blocklisted
                </MantineUnstyledButton>
              </MantineBox>
              <MantineBox className={styles.filterToggle}>
                <MantineUnstyledButton
                  type="button"
                  role="switch"
                  aria-checked={excludeAuctions}
                  className={styles.filterToggleButton}
                  onClick={() => setExcludeAuctions(!excludeAuctions)}
                >
                  <MantineText
                    span
                    className={`${styles.toggleTrack}${excludeAuctions ? ` ${styles.toggleTrackActive}` : ""}`}
                  >
                    <MantineText span className={styles.toggleKnob} />
                  </MantineText>
                  Exclude Auctions
                </MantineUnstyledButton>
              </MantineBox>
            </MantineBox>
            <DropdownPrice
              title={`PRICE${activeFilters["currency"]?.[0]?.value ? ` (${activeFilters["currency"][0].value})` : ""}`}
              icon={<PriceIcon color="#000" width="30" height="30" />}
              options={(activeFilters["price"] || []) as Filter[]}
              onSelect={(type, value) => updateActiveFilter(type, value)}
              currency={(activeFilters["currency"]?.[0]?.value as string) || "USD"}
              searchMinPrice={itemMinPrice}
              searchMaxPrice={itemMaxPrice}
            />
          </MantineBox>
          {(!!filteredActiveFilters?.length ||
            (!imageSearchMode && searchText) ||
            excludeRelists ||
            excludeAuctions) && (
            <MantineBox className={styles.tags}>
              <MantineTitle order={3} className={styles.subTitle}>
                ACTIVE FILTERS:
              </MantineTitle>
              <MantineBox className={styles.tagContainer}>
                {filteredActiveFilters.map((item) => (
                  <Tag
                    key={`${item?.dropdown}-${item?.option?.value}${item?.option?.size ? `-${item?.option?.size}` : ""}`}
                    onClick={removeFilter}
                    item={item}
                  />
                ))}
                {!imageSearchMode && searchText && (
                  <Tag
                    onClick={() =>
                      updateKeywordSearchStore(
                        "",
                        user as User | undefined,
                        savedFilters || [],
                        updateSavedFilter
                      )
                    }
                    item={{
                      dropdown: "searchTerm",
                      option: { id: 1, label: searchText, value: searchText, state: "include" },
                    }}
                  />
                )}
                {excludeRelists && (
                  <Tag
                    onClick={() => setExcludeRelists(false)}
                    item={{
                      dropdown: "excludeRelists",
                      option: {
                        id: 1,
                        label: "Exclude Blocklisted",
                        value: "true",
                        state: "include",
                      },
                    }}
                  />
                )}
                {excludeAuctions && (
                  <Tag
                    onClick={() => setExcludeAuctions(false)}
                    item={{
                      dropdown: "excludeAuctions",
                      option: {
                        id: 1,
                        label: "Exclude Auctions",
                        value: "true",
                        state: "include",
                      },
                    }}
                  />
                )}
              </MantineBox>
            </MantineBox>
          )}
        </MantineBox>
      </SlideIn>
    </>
  );
};

export default Filters;
